import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  AuthError,
} from 'firebase/auth';
import { auth } from '../firebaseConfig';
import { useAdminAuth } from '../contexts/AdminContext';
import { Eye, EyeOff, Shield, Clock } from 'lucide-react';
import {
  classifyAuthError,
  clearAppCheckThrottleState,
  hashEmail,
  type AppCheckErrorContext,
} from '../lib/appCheckRecovery';
import AppCheckRecoveryBanner from '../components/auth/AppCheckRecoveryBanner';
import { posthog } from '../providers/PostHogProvider';
import {
  isMfaRequiredError,
  buildMfaResolver,
  setPendingMfaResolver,
  clearPendingMfaResolver,
} from '../lib/nativeMfa';
import {
  isMfaEnrolmentRequiredSignInError,
  MFA_REQUIRED_SIGNIN_FULL_MESSAGE,
} from '../lib/mfaErrors';

type ErrorCtx =
  | { kind: 'generic'; message: string }
  | { kind: 'appcheck'; ctx: AppCheckErrorContext };

// Defaults ON for the admin portal: when reCAPTCHA Enterprise throttles a
// browser (a transient 403 self-throttles the App Check SDK for 24h), the login
// surfaces a self-service "Reset App Check" banner instead of a generic
// "An unknown error occurred". Kill switch for the UX itself: `?recoveryUx=off`
// or localStorage['valutten:recoveryUx']='off'.
const APP_CHECK_RECOVERY_UX: boolean = (() => {
  if (typeof window === 'undefined') return false;
  const param = new URLSearchParams(window.location.search).get('recoveryUx');
  if (param === 'off') return false;
  if (param === 'on') return true;
  try {
    const stored = localStorage.getItem('valutten:recoveryUx');
    if (stored === 'off') return false;
    if (stored === 'on') return true;
  } catch {
    // localStorage may throw in privacy modes; fall through to the default.
  }
  return true;
})();

const captureAuthFailure = async (
  emailValue: string,
  ctx: AppCheckErrorContext,
  method: 'password' | 'google',
): Promise<void> => {
  try {
    const hashedEmail = emailValue ? await hashEmail(emailValue) : '';
    const errorLike = ctx.raw as { code?: string; message?: string } | null;
    posthog.capture('auth.failure', {
      app: 'admin',
      method,
      hashed_email: hashedEmail || undefined,
      is_app_check_failure: ctx.isAppCheckFailure,
      throttle_remaining_ms: ctx.throttleRemainingMs,
      is_initial_throttle: ctx.isInitialThrottle,
      error_code: errorLike?.code,
      error_message_present: Boolean(errorLike?.message),
    });
  } catch {
    // Never let telemetry break the login path.
  }
};

const GoogleIcon = () => (
    <svg className="w-5 h-5" aria-hidden="true" focusable="false" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 488 512">
        <path fill="currentColor" d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 126 23.4 172.9 61.9l-76.3 64.5C308.6 106.5 280.2 96 248 96c-88.8 0-160.1 71.1-160.1 160.1s71.3 160.1 160.1 160.1c98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"></path>
    </svg>
);

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorCtx, setErrorCtx] = useState<ErrorCtx | null>(null);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { isFounder, loading: authLoading } = useAdminAuth();
  const passwordHideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sessionExpired = new URLSearchParams(location.search).get('expired') === 'true';

const from = location.state?.from?.pathname || '/';

useEffect(() => {
  if (!authLoading && isFounder) {
    navigate(from, { replace: true });
  }
}, [isFounder, authLoading, navigate, from]);

  // Back on the sign-in surface: any challenge from an earlier attempt is
  // abandoned, so a stale resolver must not be there for /mfa/verify to find.
  useEffect(() => {
    clearPendingMfaResolver();
  }, []);

  // Cleanup password hide timeout on unmount
  useEffect(() => {
    return () => {
      if (passwordHideTimeoutRef.current) {
        clearTimeout(passwordHideTimeoutRef.current);
      }
    };
  }, []);

  // Handle password visibility toggle with auto-hide
  const togglePasswordVisibility = () => {
    setShowPassword(prev => {
      const newState = !prev;

      // If showing password, set timeout to hide it after 4 seconds
      if (newState) {
        if (passwordHideTimeoutRef.current) {
          clearTimeout(passwordHideTimeoutRef.current);
        }

        passwordHideTimeoutRef.current = setTimeout(() => {
          setShowPassword(false);
        }, 4000); // 4 seconds timeout
      } else {
        // If hiding password manually, clear the timeout
        if (passwordHideTimeoutRef.current) {
          clearTimeout(passwordHideTimeoutRef.current);
        }
      }

      return newState;
    });
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorCtx(null);
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // AdminContext handles MFA enforcement (setup/verify redirects)
      navigate('/');
    } catch (err: unknown) {
      // Native GCIP second-factor challenge required — capture the live resolver
      // (it cannot survive router state) and hand off to the native challenge.
      if (isMfaRequiredError(err)) {
        setPendingMfaResolver(buildMfaResolver(auth, err));
        navigate('/mfa/verify');
        return;
      }
      // The MFA blocking function refused this sign-in. It throws
      // HttpsError('permission-denied', MSG_MFA_REQUIRED) from beforeUserSignedIn,
      // which the web SDK surfaces as `auth/internal-error` with the server text
      // inside `message`. Without this branch the generic arm below printed the
      // raw platform string, which reads as an outage rather than a policy
      // decision. The literal is pinned to the server constant by
      // mfaErrors.wireConstants.test.ts.
      if (isMfaEnrolmentRequiredSignInError(err)) {
        setErrorCtx({ kind: 'generic', message: MFA_REQUIRED_SIGNIN_FULL_MESSAGE });
        return;
      }
      const classification = classifyAuthError(err);
      void captureAuthFailure(email, classification, 'password');

      if (APP_CHECK_RECOVERY_UX && classification.isAppCheckFailure) {
        setErrorCtx({ kind: 'appcheck', ctx: classification });
      } else {
        const authError = err as AuthError;
        let friendlyMessage = "An unknown error occurred. Please try again.";
        switch (authError.code) {
          case 'auth/user-not-found':
          case 'auth/wrong-password':
          case 'auth/invalid-credential':
            friendlyMessage = 'Invalid email or password. Please try again.';
            break;
          case 'auth/too-many-requests':
            friendlyMessage = 'Access temporarily disabled. Please try again later.';
            break;
          default:
            if (authError.code === 'auth/invalid-api-key' ||
               (authError.message && authError.message.includes('requests-from-referer'))) {
              friendlyMessage = 'This site is not authorised to use the configured Firebase API key. Update API key restrictions.';
            }
    // console.error('Firebase Email/Password Auth Error:', authError);
            break;
        }
        setErrorCtx({ kind: 'generic', message: friendlyMessage });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setErrorCtx(null);
    setGoogleLoading(true);
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      // AdminRouteGuard routes a founder without a factor to /mfa/setup.
      navigate('/');
    } catch (err: unknown) {
      // Native GCIP second-factor challenge required — a Google-provider founder
      // with an enrolled factor lands here. Previously this branch was missing,
      // so the challenge dead-ended in a generic "Could not sign in" error.
      if (isMfaRequiredError(err)) {
        setPendingMfaResolver(buildMfaResolver(auth, err));
        navigate('/mfa/verify');
        return;
      }
      // Same refusal, reached through the Google popup: a provider sign-in IS
      // evaluated by the blocking function, so an unenrolled in-scope Google
      // account lands here rather than in the callable.
      if (isMfaEnrolmentRequiredSignInError(err)) {
        setErrorCtx({ kind: 'generic', message: MFA_REQUIRED_SIGNIN_FULL_MESSAGE });
        return;
      }
      const classification = classifyAuthError(err);
      void captureAuthFailure(email, classification, 'google');

      if (APP_CHECK_RECOVERY_UX && classification.isAppCheckFailure) {
        setErrorCtx({ kind: 'appcheck', ctx: classification });
      } else {
        const friendlyMessage = "Could not sign in with Google. Please try again.";
        setErrorCtx({ kind: 'generic', message: friendlyMessage });
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleAppCheckReset = async (): Promise<void> => {
    await clearAppCheckThrottleState();
    window.location.assign('/login');
  };


  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-900 bg-gradient-to-br from-indigo-900/50 via-gray-900 to-fuchsia-900/50 text-white">
      {/* (Existing background elements are unchanged) */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div className="absolute top-0 -left-1/4 w-96 h-96 bg-indigo-600/30 rounded-full filter blur-3xl opacity-50 animate-pulse"></div>
        <div className="absolute bottom-0 -right-1/4 w-96 h-96 bg-fuchsia-400/30 rounded-full filter blur-3xl opacity-50 animation-delay-4000 animate-pulse"></div>
      </div>

      <div className="w-full max-w-md p-8 space-y-6 rounded-2xl shadow-2xl bg-black/30 backdrop-blur-xl border border-white/10 z-10">
        <div className="text-center">
          <h1 className="text-3xl font-bold tracking-tight text-white">Founder Portal</h1>
          <p className="mt-2 text-slate-400">Secure Sign-in</p>
        </div>

        {/* Session Expired Banner */}
        {sessionExpired && !errorCtx && (
          <div className="p-3 bg-amber-500/20 border border-amber-500/30 rounded-md flex items-start gap-3">
            <Clock size={18} className="text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-300">Your session has expired due to inactivity. Please sign in again.</p>
          </div>
        )}

        <form className="space-y-6" onSubmit={handleLogin}>
          {/* Email Input */}
          <div className="space-y-4">
            <input type="email" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-lg placeholder-slate-400 text-white focus:outline-none focus:ring-2 focus:ring-fuchsia-500 transition-all duration-300" />

            {/* Password Input with Visibility Toggle */}
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 pr-12 bg-slate-900/50 border border-slate-700 rounded-lg placeholder-slate-400 text-white focus:outline-none focus:ring-2 focus:ring-fuchsia-500 transition-all duration-300"
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-white transition-colors duration-200"
                aria-label={showPassword ? "Hide password" : "Show password"}
                tabIndex={0}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            {/* Password Visibility Indicator */}
            {showPassword && (
              <p className="text-xs text-emerald-400 flex items-center mt-1">
                <Shield size={12} className="mr-1" />
                Password visible for 4 seconds
              </p>
            )}
          </div>

          {/* Forgot Password Link */}
          <div className="text-right">
            <Link
              to="/forgot-password"
              className="text-sm font-medium text-fuchsia-400 hover:text-fuchsia-300 focus:outline-none"
            >
              Forgot Password?
            </Link>
          </div>

          {errorCtx?.kind === 'generic' && (
            <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-md text-center">
              <p className="text-sm text-red-400">{errorCtx.message}</p>
            </div>
          )}
          {errorCtx?.kind === 'appcheck' && (
            <AppCheckRecoveryBanner ctx={errorCtx.ctx} onReset={handleAppCheckReset} />
          )}

          <button type="submit" disabled={loading || googleLoading} className="w-full py-3 px-4 font-semibold text-white bg-fuchsia-600 rounded-lg hover:bg-fuchsia-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-fuchsia-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300">
            {loading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>

        {/* (Existing Google Sign-In button is unchanged) */}
        <div className="relative flex items-center"><div className="flex-grow border-t border-slate-700"></div><span className="flex-shrink mx-4 text-slate-500">OR</span><div className="flex-grow border-t border-slate-700"></div></div>
        <button onClick={handleGoogleSignIn} disabled={loading || googleLoading} className="w-full flex justify-center items-center gap-3 py-3 px-4 font-semibold text-slate-300 bg-slate-800/80 border border-slate-700 rounded-lg hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-white/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300">
          <GoogleIcon />
          {googleLoading ? 'Redirecting...' : 'Sign in with Google'}
        </button>
      </div>
    </div>
  );
};

export default LoginPage;